import { FC, useEffect, useRef, useState } from 'react';

export interface FlightRosterAirportFilterValue {
  fromAirport: string;
  toAirport: string;
}

interface FlightRosterAirportFilterChipProps {
  value: FlightRosterAirportFilterValue;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onUpdate: (value: FlightRosterAirportFilterValue) => void;
}

const normalizeAirport = (value: string) => value.trim().toUpperCase();

const displayValue = ({
  fromAirport,
  toAirport,
}: FlightRosterAirportFilterValue) => {
  const from = normalizeAirport(fromAirport) || 'Any';
  const to = normalizeAirport(toAirport) || 'Any';
  return `${from} -> ${to}`;
};

const FlightRosterAirportFilterChip: FC<FlightRosterAirportFilterChipProps> = ({
  value,
  isEditing,
  onEditingChange,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      onEditingChange(false);
    };

    if (isEditing) document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, onEditingChange]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">Airport:</span>
        <span className="max-w-32 truncate">{displayValue(localValue)}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            <label className="text-sm font-medium text-gray-700">
              From airport
              <input
                aria-label="From airport"
                value={localValue.fromAirport}
                maxLength={3}
                onChange={(event) =>
                  setLocalValue((current) => ({
                    ...current,
                    fromAirport: event.target.value.toUpperCase(),
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 text-sm uppercase shadow-sm"
                placeholder="AUS"
              />
            </label>

            <label className="text-sm font-medium text-gray-700">
              To airport
              <input
                aria-label="To airport"
                value={localValue.toAirport}
                maxLength={3}
                onChange={(event) =>
                  setLocalValue((current) => ({
                    ...current,
                    toAirport: event.target.value.toUpperCase(),
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 text-sm uppercase shadow-sm"
                placeholder="DFW"
              />
            </label>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() =>
                  setLocalValue({ fromAirport: '', toAirport: '' })
                }
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdate({
                    fromAirport: normalizeAirport(localValue.fromAirport),
                    toAirport: normalizeAirport(localValue.toAirport),
                  });
                  onEditingChange(false);
                }}
                className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightRosterAirportFilterChip;
